<template>
  <div class="flex pb-4 justify-between bg-transparent">
    <!-- Dropdowns and Regenerate Report button -->
    <div class="px-4 w-2/5">
      <p>Dedicated Service Agreements</p>
      <select
        v-model="selectedContract"
        class="w-full shadow form-select appearance-none block px-3 py-1.5 text-base font-normal text-gray-700 bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-cyan-600 focus:outline-none"
        :disabled="!isAuthenticated"
        :class="{'bg-gray-200': !isAuthenticated, 'bg-white': isAuthenticated}"
      >
        <option
          disabled
          value=""
        >
          Select Company
        </option>
        <option
          v-for="contract in contracts"
          :key="contract.company.id"
          :value="contract.id"
        >
          {{ contract.company.name }}
        </option>
      </select>
    </div>

    <div class="px-5 w-1/5">
      <p>Report Frequency</p>
      <select
        v-model="selectedFrequency"
        class="w-full shadow form-select appearance-none block px-3 py-1.5 text-base font-normal text-gray-700 bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-cyan-600 focus:outline-none"
        :disabled="!isAuthenticated"
        :class="{'bg-gray-200': !isAuthenticated, 'bg-white': isAuthenticated}"
      >
        <option
          disabled
          value=""
        >
          Select Frequency
        </option>
        <option
          v-for="frequency in frequencies"
          :key="frequency.id"
          :value="frequency.id"
        >
          {{ frequency.label }}
        </option>
      </select>
    </div>

    <div class="px-5 w-1/5">
      <p>Total Budgeted</p>
      <select
        v-model="budgetSource"
        class="w-full shadow form-select appearance-none block px-3 py-1.5 text-base font-normal text-gray-700 bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-cyan-600 focus:outline-none"
        :disabled="!isAuthenticated || !selectedContract || !hasAllowanceBudget"
        :class="{ 'bg-gray-200': !isAuthenticated || !selectedContract || !hasAllowanceBudget, 'bg-white': isAuthenticated && selectedContract && hasAllowanceBudget }"
      >
        <option value="periods">
          Period Allowance
        </option>
        <option value="allowance">
          Invoiced Allowance
        </option>
      </select>
      <p
        v-if="selectedContract && !hasAllowanceBudget"
        class="mt-1 text-xs text-gray-500"
      >
        Contract allowance is unavailable for this agreement.
      </p>
    </div>

    <div class="px-5 w-1/5">
      <p>Regenerate Report</p>
      <button
        class="w-full shadow px-4 py-2 text-white rounded transition-colors"
        :class="{
          'bg-gray-500': isRegenerateDisabled,
          'bg-cyan-700 hover:bg-cyan-500': !isRegenerateDisabled
        }"
        :disabled="isRegenerateDisabled"
        @click="regenerateReport"
      >
        REGENERATE REPORT
      </button>
    </div>
  </div>
</template>

<script>
import { useDsaReportsStore } from '../pinia/dsaReportsStore';
import { useDsaReportsTicketsStore } from '../pinia/dsaReportsTicketsStore';
import { useDsaReportsProjectsStore } from '../pinia/dsaReportsProjectsStore';
import { useAuthStore } from '../pinia/authStore';
import { useDsaReportsPeriodsStore } from '../pinia/dsaReportsPeriodsStore';
import { onMounted, computed, watch } from 'vue';

export default {
  name: 'CompanySelector',

  setup() {
    const myStore      = useDsaReportsStore();
    const ticketStore  = useDsaReportsTicketsStore();
    const projectStore = useDsaReportsProjectsStore();
    const authStore    = useAuthStore();
    const periodStore  = useDsaReportsPeriodsStore();

    const isAuthenticated = computed(() => authStore.isAuthenticated);

    // Fetch contracts initially and when auth changes
    const fetchContracts = async () => {
      if (authStore.isAuthenticated) {
        await myStore.fetchContracts();
      }
    };
    onMounted(fetchContracts);
    watch(() => authStore.isAuthenticated, fetchContracts);

    // Keep frequency in sync with selected contract
    watch(
      () => myStore.selectedContract,
      (newId) => {
        const contract = myStore.contracts.find(c => c.id === newId);
        if (contract) {
          myStore.selectFrequency(myStore.getDefaultFrequencyForContract(contract));
        }
      },
      { immediate: true }
    );

    return {
      myStore,
      ticketStore,
      projectStore,
      periodStore,
      isAuthenticated,
    };
  },

  computed: {
    contracts() {
      return [...useDsaReportsStore().contracts]
        .sort((a, b) => a.company.name.localeCompare(b.company.name));
    },
    frequencies() {
      return useDsaReportsStore().frequencies;
    },
    selectedContract: {
      get() {
        return useDsaReportsStore().selectedContract;
      },
      set(value) {
        useDsaReportsStore().selectContract(value);
      }
    },
    selectedFrequency: {
      get() {
        return useDsaReportsStore().selectedFrequency;
      },
      set(value) {
        useDsaReportsStore().selectFrequency(value);
      }
    },
    budgetSource: {
      get() {
        return useDsaReportsPeriodsStore().budgetSource;
      },
      set(value) {
        useDsaReportsPeriodsStore().setBudgetSource(value);
      }
    },
    hasAllowanceBudget() {
      const contract = useDsaReportsStore().contracts.find((item) => item.id === this.selectedContract);
      return Number.isFinite(Number(contract?.periodTemplate?.allowanceTime));
    },
    isRegenerateDisabled() {
      return !this.selectedContract || !this.selectedFrequency || !this.isAuthenticated;
    }
  },

  methods: {
    async regenerateReport() {
      // Re-fetch tickets and projects with current params
      await this.ticketStore.fetchTicketsFromActivities();
      await this.projectStore.fetchProjectsFromActivities();
      // Notify parent to open/refresh the preview
      this.$emit('open-preview');
    }
  }
};
</script>

<style scoped>
/* no changes */
</style>
